/**
 * CustomGrid component
 */
import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { mapValues, isFunction, merge, pick } from 'lodash';

import { commonGridFrameworkComponents } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-grid';
import { AnyObject } from '@shared/types';
import { xviFrameworkComponents } from './cell-helpers';

type AgGridEventListener = (event: AnyObject, ...args: any[]) => void;

/**
 * Defines the component for ag-grid wrapping
 */
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-grid',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./xvi-grid.component.scss'],
  templateUrl: './xvi-grid.component.html',
})
export class XVIGridComponent implements OnInit {
  @Input()
  public set XVIGridClass(value: string) {
    this._XVIGridClass = value;
  }

  @Input()
  public set rowData(value: any[]) {
    this._rowData = value;
  }

  @Input()
  public set columnDefs(value: any[]) {
    this._columnDefs = value || [];
  }

  @Input()
  public set gridReadyResolver(value: Function) {
    this._gridReadyResolver = value;
  }

  @Input()
  public set gridOptions(value: GridOptions) {
    this.originalGridOptions = value;
    this._gridOptions = this.decorateAgGridEventsWithNgZone(value);
  }

  @Output()
  public onFiltersApply: EventEmitter<any> = new EventEmitter(true);

  public get XVIGridClass(): string {
    return this._XVIGridClass;
  }
  public get rowData(): any[] {
    return this._rowData;
  }
  public get gridReadyResolver(): Function {
    return this._gridReadyResolver;
  }

  public get gridOptions(): GridOptions {
    return this._gridOptions;
  }
  public get columnDefs(): any[] {
    return this._columnDefs;
  }

  public frameworkComponents: any = {};
  public _gridReadyResolver?: Function;
  public _gridOptions?: GridOptions;
  public _XVIGridClass?: string;
  /**
   * Contains link to the original (passed though the @Input()) GridOptions object to have an ability to provide
   * {@link GridOptions.api} & {@link GridOptions.columnApi} fields into the parent component.
   */
  private originalGridOptions?: GridOptions;
  private _rowData?: any[];
  private _columnDefs?: any[];

  public constructor(private readonly ngZone: NgZone) {}

  public ngOnInit(): void {
    this._gridOptions = {
      headerHeight: 40,
      ...this._gridOptions,
      defaultColDef: {
        resizable: true,
        ...(this._gridOptions?.defaultColDef ?? {}),
      },
    };
    xviFrameworkComponents.forEach((element) => {
      this.frameworkComponents[element.key] = element;
    });

    // More xux renderers can be picked here
    const xuxGridComponents = pick(
      commonGridFrameworkComponents,
      'booleanRenderer',
    );
    merge(this.frameworkComponents, xuxGridComponents);
  }

  public onGridReady(event: GridReadyEvent): void {
    // Providing Grid APIs to the components that uses this wrapper.
    this.originalGridOptions.api = event.api;
    this.originalGridOptions.columnApi = event.columnApi;

    if (this._gridReadyResolver) this._gridReadyResolver(event);
  }

  /**
   * AgGrid listens to some event out of Angular's Zone.
   * An example of such event is row selection handled by .onSelectionChanged().
   * The problem can be reproduced with XXVI_21962 AQA test.
   * The issue was faced AgGrid on v22.1.1 (both angular & community packages).
   */
  private decorateAgGridEventsWithNgZone(options: GridOptions): GridOptions {
    const isEventListener = (
      fnOrOtherValue: AgGridEventListener | unknown,
      key: keyof any,
    ): fnOrOtherValue is AgGridEventListener =>
      typeof key === 'string' &&
      key.startsWith('on') &&
      isFunction(fnOrOtherValue);

    return mapValues(options, (fnOrOtherValue, key) =>
      isEventListener(fnOrOtherValue, key)
        ? this.wrapFnWithNgZoneCall(fnOrOtherValue)
        : fnOrOtherValue,
    );
  }

  private wrapFnWithNgZoneCall(fn: AgGridEventListener): typeof fn {
    return (event: AnyObject, ...args: any[]): void =>
      NgZone.isInAngularZone()
        ? fn(event, ...args)
        : this.ngZone.run(() => fn(event, ...args));
  }
}
